import React, { useRef, useState, useEffect } from 'react';
import { getArticlesByLanguage, BlogArticle } from '@/data/blogArticles';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Footer } from '@/components/Footer';
import { PageTransition } from '@/components/PageTransition';
import { CardTemplatePreview } from '@/components/CardTemplatePreview';
import { HeroProductSlider } from '@/components/HeroProductSlider';
import { FeaturedProductsCarousel } from '@/components/FeaturedProductsCarousel';
import { PromoSlider } from '@/components/PromoSlider';
import { Button } from '@/components/ui/button';
import { cardTemplates } from '@/data/cardTemplates';
import { getLocalizedTestimonials } from '@/data/i18n/testimonialsContent';
import heroImage from '@/assets/hero-header.webp';
import hologramsIcon from '@/assets/icons/holograms.svg';
import scannableIcon from '@/assets/icons/scannable.svg';
import cubeMediaLogo from '@/assets/partners/cube-media-logo.svg';
import cubeMusicLogo from '@/assets/partners/cube-music-logo.svg';
import theSultanLogo from '@/assets/partners/the-sultan-logo.svg';
import drawingArtLogo from '@/assets/partners/drawingart-logo.svg';
import { SEOHead } from '@/components/SEOHead';
import { OrganizationSchema, WebsiteSchema, FAQSchema, SiteNavigationSchema } from '@/components/StructuredData';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { FeatureModal } from '@/components/modals';
import { HomeSEOContent } from '@/components/HomeSEOContent';
import { indexContent } from '@/data/i18n/indexContent';
import {
  Star, 
  Users, 
  Package,
  ShieldCheck,
  ChevronRight,
  Quote,
  Truck,
  Palette,
  Upload,
} from 'lucide-react';
import { prefetchRoute, prefetchCriticalChunks } from '@/components/LocalizedRoutes';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const statIcons = [Users, Star, Package, ShieldCheck];

/* CSS-only scroll-reveal — no framer-motion overhead during scroll */
const Reveal = ({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (delay > 0) {
            setTimeout(() => el.classList.add('revealed'), delay * 1000);
          } else {
            el.classList.add('revealed');
          }
          observer.unobserve(el);
        }
      },
      { rootMargin: '-60px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={`css-reveal ${className}`}>
      {children}
    </div>
  );
};

const Index = () => {
  const { t, language } = useLanguage();
  const localizedTestimonials = getLocalizedTestimonials(language as any);
  const { data: seoMeta } = useSeoMeta('home');
  const c = indexContent[language] || indexContent.en;
  const [blogArticles, setBlogArticles] = useState<BlogArticle[]>([]);

  useEffect(() => {
    getArticlesByLanguage(language).then(articles => setBlogArticles(articles.slice(0, 3)));
  }, [language]);

  // Prefetch Shop + Product chunks during idle time
  useEffect(() => {
    prefetchCriticalChunks();
  }, []);

  const stepIcons = [Palette, Upload, Truck];

  return (
    <PageTransition>
      <SEOHead 
        title={seoMeta?.title}
        description={seoMeta?.description || undefined}
        keywords={seoMeta?.keywords || undefined}
        ogTitle={seoMeta?.ogTitle || undefined}
        ogDescription={seoMeta?.ogDescription || undefined}
        canonicalPath={seoMeta?.canonicalPath || '/'}
        language={language}
      />
      <OrganizationSchema />
      <WebsiteSchema />
      <SiteNavigationSchema />
      <FAQSchema items={c.faq.items} />
      
      <div className="min-h-screen flex flex-col bg-background">
        
        <main className="flex-1">
          {/* ─── HERO — Immersive full-bleed ─── */}
          <section className="relative min-h-[72vh] flex items-center overflow-x-clip pt-28 md:pt-24 lg:pt-40 pb-8 lg:pb-16">
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-transparent z-10" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50 z-10" />
            
            <div className="absolute inset-0">
              <img 
                src={heroImage}
                alt="Collection of premium ID cards"
                className="w-full h-full object-cover object-center"
                fetchPriority="high"
                decoding="async"
                loading="eager"
              />
            </div>
            
            <div className="container relative z-20 overflow-visible">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center overflow-visible">
                <div className="space-y-6 overflow-visible">
                  <div className="space-y-4">
                    <h1 className="display-text tracking-tight text-center sm:text-left break-words" style={{ fontSize: 'clamp(2.5rem, 7vw, 5.5rem)', lineHeight: 0.95 }}>
                      <span className="block">{c.heroTitle1}</span>
                      <span className="block">
                        {c.heroTitle2 ? <>{c.heroTitle2} </> : null}
                        <span className="text-gradient-coral">{c.heroTitleHighlight}</span>
                      </span>
                    </h1>
                  </div>
                  
                  <div className="py-4 overflow-visible">
                    <HeroProductSlider />
                  </div>
                </div>
                
                <div className="hidden lg:block" aria-hidden="true" />
              </div>
            </div>
          </section>

          {/* ─── STATS BAR ─── */}
          <section className="border-y border-border py-8 md:py-10 lg:mt-16">
            <div className="container">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12">
                {c.stats.map((stat, index) => {
                  const Icon = statIcons[index];
                  return (
                    <div 
                      key={index} 
                      className="flex flex-col items-center text-center md:flex-row md:text-left md:items-center gap-3"
                    >
                      <Icon className="h-5 w-5 md:h-6 md:w-6 flex-shrink-0 text-primary" />
                      <div>
                        <div className="display-text text-2xl md:text-3xl text-foreground leading-tight">{stat.value}</div>
                        <div className="text-[10px] md:text-sm text-muted-foreground uppercase tracking-wider leading-tight">{stat.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ─── FEATURED PRODUCTS ─── */}
          <FeaturedProductsCarousel />

          {/* ─── HOW IT WORKS — Cuberto-style numbered steps ─── */}
          <section className="pt-8 pb-20 md:pt-10 md:pb-28 bg-background overflow-hidden">
            <div className="container">
              <Reveal>
                <div className="mb-12 md:mb-20 max-w-5xl mx-auto text-center">
                  <h2 className="display-text text-4xl sm:text-5xl md:text-6xl text-foreground">
                    {c.howItWorks.title}
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto mt-4 leading-relaxed">
                    {c.howItWorks.subtitle}
                  </p>
                </div>
              </Reveal>

              {/* Desktop: full-width staggered rows / Mobile: horizontal scroll */}
              <div className="flex md:grid md:grid-cols-3 gap-4 md:gap-8 mx-auto overflow-x-auto overflow-y-hidden md:overflow-visible snap-x snap-mandatory scrollbar-hide pb-0 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
                {c.howItWorks.steps.map((step, index) => (
                  <Reveal
                    key={index}
                    className="group relative flex-shrink-0 w-[80vw] sm:w-[60vw] md:w-auto snap-center"
                    delay={index * 0.15}
                  >
                    <div className="relative h-full rounded-2xl border border-border bg-card overflow-hidden p-8 md:p-10 hover:border-primary/40 transition-all duration-500">
                      {/* Big step number */}
                      <div className="absolute -top-4 -right-2 display-text text-[8rem] md:text-[10rem] leading-none text-primary/[0.04] pointer-events-none select-none">
                        {String(index + 1).padStart(2, '0')}
                      </div>

                      {/* Accent line */}
                      <div className="w-10 h-1 rounded-full bg-primary mb-8 group-hover:w-16 transition-all duration-500" />

                      <div className="relative z-10 space-y-3">
                        <span className="text-xs font-mono text-primary uppercase tracking-widest">
                          Step {String(index + 1).padStart(2, '0')}
                        </span>
                        <h3 className="font-display font-bold text-xl md:text-2xl text-foreground">{step.title}</h3>
                        <p className="text-muted-foreground leading-relaxed text-base">{step.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* ─── MARQUEE 1 ─── */}
          <section className="py-6 overflow-hidden bg-background">
            <div className="flex animate-marquee whitespace-nowrap">
              <span className="display-text text-7xl md:text-9xl text-muted/20 mx-8" aria-hidden="true">
                Biography • Identity • Premium • Quality • Biography • Identity • Premium • Quality •
              </span>
            </div>
          </section>

          {/* ─── HOLOGRAMS ─── */}
          <section className="py-24 md:py-32 lg:py-40 bg-background">
            <div className="container">
              <div className="max-w-5xl mx-auto">
                <Reveal>
                  <p className="text-muted-foreground uppercase tracking-widest text-sm mb-8">
                    {c.holograms.subtitle} <span className="text-primary">{c.holograms.subtitleHighlight}</span>{c.holograms.subtitleEnd}
                  </p>
                </Reveal>
                
                <div className="space-y-2 md:space-y-4 mb-10">
                  <Reveal delay={0.1}>
                    <FeatureModal feature="holograms">
                      <button className="flex items-center gap-4 md:gap-6 group cursor-pointer bg-transparent border-none p-0 text-left">
                        <h2 className="display-text text-4xl sm:text-6xl md:text-8xl lg:text-9xl text-foreground group-hover:text-primary transition-colors">
                          Holograms
                        </h2>
                        <img 
                          src={hologramsIcon} 
                          alt="Hologram layers" 
                          width={128} height={128}
                          className="h-12 sm:h-20 md:h-28 lg:h-32 w-auto transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 group-hover:drop-shadow-[0_0_20px_rgba(247,93,78,0.3)]"
                        />
                      </button>
                    </FeatureModal>
                  </Reveal>
                  <Reveal delay={0.2}>
                    <FeatureModal feature="scannable">
                      <button className="flex items-center gap-4 md:gap-6 group cursor-pointer bg-transparent border-none p-0 text-left">
                        <img 
                          src={scannableIcon} 
                          alt="Scannable QR code" 
                          width={112} height={112}
                          className="h-10 sm:h-16 md:h-24 lg:h-28 w-auto text-primary transition-all duration-500 group-hover:scale-110 group-hover:-rotate-3 group-hover:drop-shadow-[0_0_20px_rgba(247,93,78,0.4)]"
                          style={{ filter: 'brightness(0) saturate(100%) invert(45%) sepia(63%) saturate(1016%) hue-rotate(327deg) brightness(97%) contrast(96%)' }}
                        />
                        <h2 className="display-text text-4xl sm:text-6xl md:text-8xl lg:text-9xl text-primary group-hover:opacity-80 transition-opacity">
                          Scannable
                        </h2>
                      </button>
                    </FeatureModal>
                  </Reveal>
                </div>
                
                <Reveal delay={0.3}>
                  <p className="text-muted-foreground max-w-xl text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: c.holograms.scanInfo }} />
                </Reveal>
              </div>
            </div>
          </section>

          {/* ─── PROMO SLIDER ─── */}
          <PromoSlider />

          {/* ─── DISCRETE DELIVERY — Cuberto-style compact ─── */}
          <section className="py-16 md:py-24 bg-background">
            <div className="container">
              <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-center">
                <Reveal>
                  <FeatureModal feature="shipping">
                    <button className="cursor-pointer group bg-transparent border-none p-0 text-left">
                      <h2 className="display-text text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                        <span className="text-muted-foreground group-hover:text-foreground transition-colors">{c.delivery.line1}</span>
                        <br />
                        <span className="text-primary/80">{c.delivery.line2}</span>
                        <br />
                        <span className="text-primary group-hover:text-primary/80 transition-colors">{c.delivery.line3}</span>
                      </h2>
                    </button>
                  </FeatureModal>
                </Reveal>
                
                <Reveal delay={0.15}>
                  <div className="space-y-6">
                    <FeatureModal feature="quality">
                      <button className="text-muted-foreground text-base md:text-lg leading-relaxed cursor-pointer hover:text-foreground transition-colors bg-transparent border-none p-0 text-left">
                        {c.delivery.description}
                      </button>
                    </FeatureModal>
                    <Button size="lg" asChild className="px-8">
                      <LocalizedLink to="/shop">
                        {c.delivery.cta}
                      </LocalizedLink>
                    </Button>
                  </div>
                </Reveal>
              </div>
            </div>
          </section>

          {/* ─── TESTIMONIALS ─── */}
          <section className="py-24 md:py-32 section-darker cv-auto">
            <div className="container">
              <Reveal>
                <div className="text-center mb-16 md:mb-20 space-y-4">
                  <h2 className="display-text text-4xl sm:text-5xl md:text-6xl text-foreground">
                    {c.testimonials.title}{' '}
                    <span className="text-gradient-coral">{c.testimonials.titleHighlight}</span>
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                    {c.testimonials.subtitle}
                  </p>
                </div>
              </Reveal>
              
              <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
                {localizedTestimonials.slice(0, 3).map((review, index) => (
                  <Reveal key={index} delay={index * 0.1}>
                    <div className="group bg-card border border-border rounded-2xl p-8 space-y-5 transition-all duration-500 hover:border-primary/50 h-full">
                      <Quote className="h-8 w-8 text-primary/30" />
                      <div className="flex items-center gap-1">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                        ))}
                      </div>
                      <p className="text-foreground italic text-lg leading-relaxed">"{review.text}"</p>
                      <div className="flex items-center gap-3 pt-2">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                          {review.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-display font-bold text-foreground">{review.name}</div>
                          <div className="text-xs text-muted-foreground">{review.location}</div>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
              
              <Reveal delay={0.3}>
                <div className="text-center mt-12">
                  <Button variant="outline" size="lg" asChild className="gap-2">
                    <LocalizedLink to="/reviews">
                      {c.testimonials.cta}
                      <ChevronRight className="h-4 w-4" />
                    </LocalizedLink>
                  </Button>
                </div>
              </Reveal>
            </div>
          </section>

          {/* ─── MARQUEE 2 ─── */}
          <section className="py-6 overflow-hidden bg-background">
            <div className="flex animate-marquee whitespace-nowrap" style={{ animationDirection: 'reverse' }}>
              <span className="display-text text-7xl md:text-9xl text-muted/20 mx-8" aria-hidden="true">
                Biography • Identity • Premium • Quality • Biography • Identity • Premium • Quality •
              </span>
            </div>
          </section>

          {/* ─── BLOG ─── */}
          <section className="py-24 md:py-32 bg-background cv-auto">
            <div className="container">
              <Reveal>
                <div className="text-center mb-16 md:mb-20 space-y-4">
                  <h2 className="display-text text-4xl sm:text-5xl md:text-6xl text-foreground">
                    {c.blog.title}{' '}
                    <span className="text-gradient-coral">{c.blog.titleHighlight}</span>
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                    {c.blog.subtitle}
                  </p>
                </div>
              </Reveal>
              
              <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                {blogArticles.map((article, index) => (
                  <Reveal key={article.slug} delay={index * 0.1}>
                    <LocalizedLink
                      to={`/blog/${article.slug}`}
                      className="group block bg-card border border-border rounded-2xl overflow-hidden transition-all duration-500 hover:border-primary/50 h-full"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={article.image}
                          alt={article.imageAlt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-6 md:p-8 space-y-4">
                        <h3 className="font-display text-xl font-bold text-foreground line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-muted-foreground text-sm line-clamp-3 leading-relaxed">
                          {article.teaser}
                        </p>
                        <div className="pt-2">
                          <span className="text-primary text-sm font-display font-bold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                            {c.blog.readMore}
                            <ChevronRight className="h-4 w-4" />
                          </span>
                        </div>
                      </div>
                    </LocalizedLink>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* ─── SEO CONTENT ─── */}
          <HomeSEOContent />

          {/* ─── FAQ ─── */}
          <section id="faq" className="py-24 md:py-32 section-darker cv-auto">
            <div className="container">
              <Reveal>
                <div className="text-center mb-16 md:mb-20 space-y-4">
                  <h2 className="display-text text-4xl sm:text-5xl md:text-6xl text-foreground">
                    {c.faq.title}{' '}
                    <span className="text-gradient-coral">{c.faq.titleHighlight}</span>
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                    {c.faq.subtitle}
                  </p>
                </div>
              </Reveal>
              
              <Reveal delay={0.15}>
                <div className="max-w-3xl mx-auto">
                  <Accordion type="single" collapsible className="space-y-3">
                    {c.faq.items.map((faq, index) => (
                      <AccordionItem 
                        key={index} 
                        value={`item-${index}`}
                        className="bg-card border border-border rounded-xl px-5 hover:border-primary/50 data-[state=open]:border-primary/50 transition-all duration-300"
                      >
                        <AccordionTrigger className="text-left hover:no-underline py-5">
                          <span className="font-display font-bold text-foreground">{faq.question}</span>
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </Reveal>
              
              <Reveal delay={0.25}>
                <div className="mt-12 text-center">
                  <p className="text-sm text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                    <span dangerouslySetInnerHTML={{ __html: c.faq.footerText }} />{' '}
                    <LocalizedLink to="/how-to" className="text-primary hover:underline">{c.faq.guideLink}</LocalizedLink>{' '}
                    {c.faq.or}{' '}
                    <LocalizedLink to="/contact" className="text-primary hover:underline">{c.faq.contactLink}</LocalizedLink>.{' '}
                    <span dangerouslySetInnerHTML={{ __html: c.faq.footerEnd }} />
                  </p>
                </div>
              </Reveal>
            </div>
          </section>

          {/* ─── PROCESS / STOPWATCH ─── */}
          <section className="py-24 md:py-32 lg:py-40">
            <div className="container">
              <div className="bg-card/30 rounded-3xl py-16 md:py-24 px-8 md:px-16 lg:px-20">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  <Reveal>
                    <div className="space-y-8">
                      <h2 className="display-text text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-foreground">
                        {c.process.title1}<br />
                        <span className="text-gradient-coral">{c.process.title2}</span>
                      </h2>
                      
                      <div className="space-y-4">
                        {c.process.steps.map((step, index) => (
                          <p key={index} className="text-lg text-muted-foreground flex items-start gap-3 leading-relaxed">
                            <span className="font-display font-bold text-primary">{index + 1}.</span>
                            {step}
                          </p>
                        ))}
                      </div>
                      
                      <Button size="lg" asChild className="px-8">
                        <LocalizedLink to="/shop">
                          {c.process.cta}
                        </LocalizedLink>
                      </Button>
                    </div>
                  </Reveal>
                  
                  {/* Stopwatch Visual */}
                  <Reveal delay={0.2}>
                    <div className="flex items-center justify-center">
                      <div className="relative">
                        <div className="w-48 h-48 md:w-64 md:h-64 rounded-full border-4 border-muted-foreground/30 bg-card flex items-center justify-center relative">
                          <div className="absolute inset-4 rounded-full border-2 border-muted-foreground/20 flex items-center justify-center">
                            {[...Array(12)].map((_, i) => (
                              <div 
                                key={i}
                                className="absolute w-1 h-3 bg-muted-foreground/40 rounded-full"
                                style={{ transform: `rotate(${i * 30}deg) translateY(-80px)` }}
                              />
                            ))}
                            <div 
                              className="absolute w-1.5 h-20 bg-primary rounded-full origin-bottom animate-[stopwatch-tick_60s_steps(60)_infinite]"
                              style={{ transform: 'translateY(-40px)' }}
                            />
                            <div className="w-4 h-4 rounded-full bg-primary" />
                          </div>
                          <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-6 h-10 bg-muted-foreground/40 rounded-t-lg" />
                          <div className="absolute -top-2 -right-2 w-4 h-6 bg-muted-foreground/30 rounded-r-lg transform rotate-45" />
                          <div className="absolute -top-2 -left-2 w-4 h-6 bg-muted-foreground/30 rounded-l-lg transform -rotate-45" />
                        </div>
                        <svg className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-32 h-16 text-muted-foreground/30" viewBox="0 0 128 64">
                          <path d="M64 0 C64 32, 0 32, 0 64" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                      </div>
                    </div>
                  </Reveal>
                </div>
              </div>
            </div>
          </section>

          {/* ─── PARTNERS (temporarily disabled – needs proper logos) ─── */}
          {/*
          <section className="py-20 md:py-24 bg-background">
            ...
          </section>
          */}
        </main>

        <Footer />
      </div>
    </PageTransition>
  );
};

export default Index;
